import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import parse from 'html-react-parser';

import { useParams } from 'react-router-dom';
import useSetPageTitle from '../../hooks/useSetPageTitle/useSetPageTitle';

import Icon from '../../components/Icon/Icon';
import PageBanner from '../../common/PageBanner/PageBanner';

import styles from './ProjectPage.module.scss';
import commons from '../../shared/styles/common.module.scss';
import PROJECTS from '../../collections/projects/projects';
import {
  DEV_BASE_URL,
  IS_DEV,
  PROD_BASE_URL,
  UPLOADS_FOLDER,
} from '../../config/constants/contants';

const ProjectPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const postId = params.projectId;
  const bannerURL = IS_DEV ? DEV_BASE_URL : PROD_BASE_URL;

  const project = useMemo(
    () => PROJECTS.find((p) => p.id === Number(postId)),
    [postId],
  );

  useSetPageTitle({
    pageTitle: `${project?.title} - ${t('general.website-name')}`,
  });

  const [openExhibits, setOpenExhibits] = useState<Set<number>>(new Set([0]));

  const toggleExhibit = (idx: number) => {
    setOpenExhibits((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  if (!project) return null;

  return (
    <div>
      <PageBanner
        title={project.title}
        imageSrc={
          project.featured_image ||
          `${bannerURL}${UPLOADS_FOLDER}/cetatea-fagaras-vechi.webp`
        }
        imageText={project.title}
      />
      <div className={classNames(styles.projectContainer, commons.container)}>
        <div className={styles.projectContent}>{parse(project.content)}</div>
        <div className={styles.projectDocs}>
          {project.exhibits.map((exhibit, idx) => {
            const isOpen = openExhibits.has(idx);

            return (
              <div
                className={styles.projectFile}
                key={`${exhibit.name}-${idx}`}>
                <div
                  className={styles.projectFileTitleWrapper}
                  onClick={() => toggleExhibit(idx)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && toggleExhibit(idx)}
                  aria-expanded={isOpen}>
                  <Icon name='fa-hashtag' color='icon-accent' size={20} />
                  <h4 className={styles.projectTitle}>{exhibit.name}</h4>
                  <span
                    className={classNames(styles.accordionChevron, {
                      [styles.accordionChevronOpen]: isOpen,
                    })}>
                    <Icon
                      name='fa-chevron-down'
                      color='icon-accent'
                      size={14}
                    />
                  </span>
                </div>
                {isOpen && (
                  <>
                    <div className={styles.projectDocsTxt}>
                      <p className={styles.projectDocsType}>Fișiere:</p>
                      {exhibit.documents
                        .filter((doc) => doc.type === 'txt')
                        .map((doc) => (
                          <div className={styles.projectDocItem} key={doc.url}>
                            <Icon
                              name='fa-file-word'
                              color='icon-accent'
                              size={16}
                            />
                            <a href={doc.url} rel='noopener noreferrer'>
                              {doc.name}
                            </a>
                          </div>
                        ))}
                    </div>
                    <div className={styles.projectDocsImg}>
                      {exhibit.documents
                        .filter((doc) => doc.type === 'img')
                        .map((doc) => (
                          <div className={styles.projectDocItem} key={doc.url}>
                            <img
                              src={doc.url}
                              alt={doc.name}
                              style={{ maxWidth: '100%' }}
                            />
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProjectPage;
